import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiClient } from '~/api/client'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { PageHeader } from '~/components/PageHeader'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Select } from '~/components/ui/select'
import { Skeleton } from '~/components/ui/skeleton'
import { Textarea } from '~/components/ui/textarea'
import { useToast } from '~/components/ui/toast'

// 프롬프트 버전 관리: 좌측 목록에서 고르면 우측 패널에서 사용처·전체 내용·활성/편집을 관리한다.
// ref(certification.coach.v1_system)는 사람이 읽는 "사용처"로 풀어 보여주고, 원문 ref는 참고 칩으로 둔다.
interface PromptVersion {
    id: string
    ref: string
    profile: string
    feature: string
    service: string
    version: number
    type: string
    content: string
    active: boolean
}

const ALL = '__all__'

// 서비스별 배지 톤(차트 의미색과 일관).
const SERVICE_TONE: Record<
    string,
    'info' | 'primary' | 'warning' | 'point' | 'neutral'
> = {
    상담: 'info',
    인증평가: 'primary',
    유지하기: 'warning',
    모니터링: 'point',
}

// 기능(feature) → 사람이 읽는 이름. 모르는 값은 밑줄만 공백으로 바꿔 폴백한다.
const FEATURE_LABEL: Record<string, string> = {
    coach: '인증 코치',
    consult: '상담 챗봇',
    assist: '모니터링 어시스턴트',
    assist_answer: '모니터링 답변 생성',
    assist_choices: '모니터링 선택지 생성',
}
const featureLabel = (feature: string) =>
    FEATURE_LABEL[feature] ?? feature.replace(/_/g, ' ')

const TYPE_LABEL: Record<string, string> = {
    system: '시스템',
    user: '사용자',
    developer: '개발자',
}
const typeLabel = (type: string) => `${TYPE_LABEL[type] ?? type} 프롬프트`

export function PromptsPage() {
    const toast = useToast()
    const [searchParams, setSearchParams] = useSearchParams()
    const [prompts, setPrompts] = useState<PromptVersion[] | null>(null)
    const [query, setQuery] = useState('')
    const [service, setService] = useState(ALL)
    const [profile, setProfile] = useState(ALL)
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [pendingActivate, setPendingActivate] =
        useState<PromptVersion | null>(null)
    // 내용 편집 슬라이드오버. 저장 시 새 버전 생성 + 활성화(PUT content+activate).
    const [editing, setEditing] = useState<PromptVersion | null>(null)
    const [draft, setDraft] = useState('')
    const [saving, setSaving] = useState(false)
    const [pendingSave, setPendingSave] = useState(false)

    const load = () => {
        apiClient
            .get<PromptVersion[]>('/prompts')
            .then(setPrompts)
            .catch((e) => {
                setPrompts([])
                toast.error((e as Error).message)
            })
    }

    useEffect(load, [])

    const profiles = useMemo(
        () => Array.from(new Set((prompts ?? []).map((p) => p.profile))).sort(),
        [prompts]
    )
    const services = useMemo(
        () => Array.from(new Set((prompts ?? []).map((p) => p.service))).sort(),
        [prompts]
    )

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        return (prompts ?? []).filter((p) => {
            if (service !== ALL && p.service !== service) return false
            if (profile !== ALL && p.profile !== profile) return false
            if (!q) return true
            return (
                p.ref.toLowerCase().includes(q) ||
                p.feature.toLowerCase().includes(q) ||
                featureLabel(p.feature).toLowerCase().includes(q) ||
                p.content.toLowerCase().includes(q)
            )
        })
    }, [prompts, service, profile, query])

    // 선택이 필터 결과에서 사라지면 첫 항목을 자동 선택한다(패널이 늘 채워지도록).
    useEffect(() => {
        if (filtered.length && !filtered.some((p) => p.id === selectedId)) {
            setSelectedId(filtered[0].id)
        }
    }, [filtered, selectedId])

    // 딥링크(`?ref=<promptRef>`): 기능 카탈로그 등에서 특정 프롬프트로 진입하면 그 계열
    // (버전 제외)의 활성본을 자동 선택한다. 필터가 가려 못 고르는 상황을 막으려 필터를
    // 초기화한 뒤 선택하고, ref는 1회 소비 후 URL에서 제거한다(이후 자유 선택을 방해하지 않게).
    useEffect(() => {
        const ref = searchParams.get('ref')
        if (!ref || !prompts) return
        const seriesKey = (r: string) => r.replace(/\.v\d+_/, '.')
        const series = prompts.filter((p) => seriesKey(p.ref) === seriesKey(ref))
        const pick =
            series.find((p) => p.active) ??
            series[0] ??
            prompts.find((p) => p.ref === ref)
        if (pick) {
            setService(ALL)
            setProfile(ALL)
            setQuery('')
            setSelectedId(pick.id)
        }
        const next = new URLSearchParams(searchParams)
        next.delete('ref')
        setSearchParams(next, { replace: true })
    }, [prompts, searchParams, setSearchParams])

    const selected = filtered.find((p) => p.id === selectedId) ?? null

    const confirmActivate = async () => {
        if (!pendingActivate) return
        const { ref } = pendingActivate
        setPendingActivate(null)
        try {
            await apiClient.put(`/prompts/${encodeURIComponent(ref)}`, {
                activate: true,
            })
            toast.success(`'${ref}' 프롬프트를 활성으로 전환했어요.`)
            load()
        } catch (err) {
            toast.error((err as Error).message)
        }
    }

    const openEditor = (p: PromptVersion) => {
        setEditing(p)
        setDraft(p.content)
    }

    // 저장: 같은 ref로 새 버전 생성 + 활성화. 에이전트는 30초 주기로 반영한다.
    const confirmSave = async () => {
        if (!editing) return
        const { ref } = editing
        setPendingSave(false)
        setSaving(true)
        try {
            await apiClient.put(`/prompts/${encodeURIComponent(ref)}`, {
                content: draft,
                activate: true,
            })
            setEditing(null)
            toast.success(
                `'${ref}' 프롬프트를 새 버전으로 저장·활성화했어요. 에이전트에 최대 30초 내 반영됩니다.`
            )
            load()
        } catch (err) {
            toast.error((err as Error).message)
        } finally {
            setSaving(false)
        }
    }

    return (
        <div>
            <PageHeader
                title='프롬프트 버전 관리'
                description='왼쪽에서 프롬프트를 고르면 오른쪽에서 사용처·전체 내용·활성/편집을 관리해요.'
            />

            <div className='mb-4 flex flex-wrap items-end gap-3'>
                <div className='flex flex-col gap-1.5'>
                    <Label htmlFor='prompt-search'>검색</Label>
                    <Input
                        id='prompt-search'
                        size='sm'
                        className='w-64'
                        placeholder='사용처 · ref · 내용'
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>
                <div className='flex flex-col gap-1.5'>
                    <Label htmlFor='prompt-service'>사용 서비스</Label>
                    <Select
                        id='prompt-service'
                        size='sm'
                        className='w-40'
                        value={service}
                        onChange={(e) => setService(e.target.value)}>
                        <option value={ALL}>전체</option>
                        {services.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className='flex flex-col gap-1.5'>
                    <Label htmlFor='prompt-profile'>프로파일</Label>
                    <Select
                        id='prompt-profile'
                        size='sm'
                        className='w-48'
                        value={profile}
                        onChange={(e) => setProfile(e.target.value)}>
                        <option value={ALL}>전체</option>
                        {profiles.map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </Select>
                </div>
                <span className='text-muted-foreground pb-2.5 text-sm'>
                    {filtered.length} / {prompts?.length ?? 0}건
                </span>
            </div>

            {prompts === null ? (
                <div className='space-y-2'>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className='h-11 w-full' />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className='border-border text-muted-foreground rounded border border-dashed py-16 text-center text-sm'>
                    조건에 맞는 프롬프트가 없어요.
                </div>
            ) : (
                <div className='flex flex-col gap-4 lg:flex-row'>
                    {/* 좌: 목록 */}
                    <div className='max-h-[68vh] shrink-0 space-y-1.5 overflow-y-auto pr-1 lg:w-[360px]'>
                        {filtered.map((p) => {
                            const isActive = p.id === selectedId
                            return (
                                <button
                                    key={p.id}
                                    type='button'
                                    onClick={() => setSelectedId(p.id)}
                                    className={`w-full rounded border px-3 py-2.5 text-left transition ${
                                        isActive
                                            ? 'border-primary bg-accent'
                                            : 'border-border bg-card hover:bg-secondary'
                                    }`}>
                                    <div className='flex items-center justify-between gap-2'>
                                        <span className='text-foreground truncate text-sm font-bold'>
                                            {featureLabel(p.feature)}
                                        </span>
                                        {p.active ? (
                                            <Badge tone='success'>활성</Badge>
                                        ) : (
                                            <Badge tone='neutral'>비활성</Badge>
                                        )}
                                    </div>
                                    <div className='mt-1 flex items-center gap-1.5'>
                                        <Badge
                                            tone={
                                                SERVICE_TONE[p.service] ??
                                                'neutral'
                                            }>
                                            {p.service}
                                        </Badge>
                                        <span className='text-muted-foreground truncate text-xs'>
                                            {p.profile} · v{p.version} ·{' '}
                                            {p.type}
                                        </span>
                                    </div>
                                </button>
                            )
                        })}
                    </div>

                    {/* 우: 상세 패널 */}
                    <div className='min-w-0 flex-1'>
                        {selected ? (
                            <div className='bg-card border-border rounded border'>
                                <div className='border-border border-b px-5 py-4'>
                                    <div className='flex flex-wrap items-center gap-2'>
                                        <h2 className='text-foreground text-lg font-bold'>
                                            {featureLabel(selected.feature)}
                                        </h2>
                                        <Badge
                                            tone={
                                                SERVICE_TONE[
                                                    selected.service
                                                ] ?? 'neutral'
                                            }>
                                            {selected.service}
                                        </Badge>
                                        {selected.active ? (
                                            <Badge tone='success'>활성</Badge>
                                        ) : (
                                            <Badge tone='neutral'>비활성</Badge>
                                        )}
                                    </div>
                                    <p className='text-muted-foreground mt-1.5 text-sm'>
                                        <b className='text-foreground'>
                                            {selected.service}
                                        </b>{' '}
                                        서비스의{' '}
                                        {featureLabel(selected.feature)} —{' '}
                                        {typeLabel(selected.type)}
                                    </p>
                                    <div className='text-muted-foreground mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs'>
                                        <span className='inline-flex items-center gap-1'>
                                            ref
                                            <code className='bg-muted text-foreground rounded px-1.5 py-0.5 font-mono'>
                                                {selected.ref}
                                            </code>
                                        </span>
                                        <span>프로파일 {selected.profile}</span>
                                        <span>기능 {selected.feature}</span>
                                        <span>버전 v{selected.version}</span>
                                    </div>
                                </div>
                                <div className='px-5 py-4'>
                                    <div className='mb-2 flex items-center justify-between'>
                                        <Label>내용 (Jinja 템플릿)</Label>
                                        <div className='flex gap-1.5'>
                                            <Button
                                                variant='secondary'
                                                size='sm'
                                                onClick={() =>
                                                    openEditor(selected)
                                                }>
                                                편집
                                            </Button>
                                            {!selected.active ? (
                                                <Button
                                                    variant='ghost'
                                                    size='sm'
                                                    onClick={() =>
                                                        setPendingActivate(
                                                            selected
                                                        )
                                                    }>
                                                    활성화
                                                </Button>
                                            ) : null}
                                        </div>
                                    </div>
                                    <pre className='bg-muted/40 border-border text-foreground max-h-[52vh] overflow-auto rounded border p-3 font-mono text-[13px] leading-relaxed whitespace-pre-wrap'>
                                        {selected.content || '(내용 없음)'}
                                    </pre>
                                </div>
                            </div>
                        ) : (
                            <div className='border-border text-muted-foreground flex h-64 items-center justify-center rounded border border-dashed text-sm'>
                                왼쪽에서 프롬프트를 선택하세요.
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={pendingActivate !== null}
                title='프롬프트 활성 전환'
                description={
                    pendingActivate
                        ? `'${pendingActivate.ref}' (v${pendingActivate.version})를 활성으로 전환할까요? 같은 ref의 기존 활성 버전은 비활성으로 바뀔 수 있어요.`
                        : undefined
                }
                confirmLabel='활성화'
                onConfirm={confirmActivate}
                onCancel={() => setPendingActivate(null)}
            />

            {/* 내용 편집 슬라이드오버 */}
            {editing ? (
                <div className='fixed inset-0 z-50 flex'>
                    <div
                        className='bg-foreground/30 flex-1'
                        onClick={() => !saving && setEditing(null)}
                        aria-hidden
                    />
                    <div className='bg-card border-border flex w-full max-w-2xl flex-col border-l shadow-xl'>
                        <div className='border-border flex items-center justify-between border-b px-5 py-4'>
                            <div>
                                <div className='flex items-center gap-2'>
                                    <h2 className='krds-title'>
                                        프롬프트 편집
                                    </h2>
                                    <Badge
                                        tone={
                                            SERVICE_TONE[editing.service] ??
                                            'neutral'
                                        }>
                                        {editing.service}
                                    </Badge>
                                </div>
                                <p className='text-muted-foreground mt-0.5 text-sm'>
                                    {featureLabel(editing.feature)} ·{' '}
                                    {editing.ref} (v{editing.version})
                                </p>
                            </div>
                            <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => setEditing(null)}
                                disabled={saving}>
                                닫기
                            </Button>
                        </div>
                        <div className='flex-1 overflow-y-auto p-5'>
                            <Label htmlFor='prompt-content'>
                                내용 (Jinja 템플릿)
                            </Label>
                            <Textarea
                                id='prompt-content'
                                className='mt-1.5 min-h-[420px] font-mono text-[14px]'
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                spellCheck={false}
                            />
                            <p className='text-muted-foreground mt-2 text-sm'>
                                저장하면 같은 ref로{' '}
                                <b>새 버전을 만들어 활성화</b>
                                합니다. 에이전트는 30초 주기로 활성 프롬프트를
                                새로고침하므로 재시작 없이 반영됩니다. 변수(
                                {'{{ ... }}'})는 유지하세요.
                            </p>
                        </div>
                        <div className='border-border flex items-center justify-end gap-2 border-t px-5 py-4'>
                            <Button
                                variant='tertiary'
                                onClick={() => setEditing(null)}
                                disabled={saving}>
                                취소
                            </Button>
                            <Button
                                onClick={() => setPendingSave(true)}
                                disabled={saving || draft === editing.content}>
                                {saving ? '저장 중…' : '저장 후 활성화'}
                            </Button>
                        </div>
                    </div>
                </div>
            ) : null}

            <ConfirmDialog
                open={pendingSave}
                title='프롬프트 새 버전 저장·활성화'
                description={
                    editing
                        ? `'${editing.ref}'를 새 버전으로 저장하고 즉시 활성화합니다. 운영 중인 에이전트 응답에 영향을 줍니다(최대 30초 내 반영). 진행할까요?`
                        : undefined
                }
                confirmLabel='저장·활성화'
                onConfirm={confirmSave}
                onCancel={() => setPendingSave(false)}
            />
        </div>
    )
}
